import React, { useState } from 'react';
import FactoryScene from './FactoryScene';
import { Button, Dropdown, Option } from "@fluentui/react-components";
import { ToggleButtonGroup } from '../components/Generic/ToggleButtonGroup';
import { ArrowSync16Regular, Dismiss20Regular, LockClosed20Regular, LockOpen20Regular, ArrowClockwise16Regular, Pause16Regular } from '@fluentui/react-icons';
import { Node } from "gl-react";
import { Surface } from "gl-react-dom";
import { Color } from 'three';
import HeatGuideShader from './Shaders/HeatGuideShader';
import { Oval } from 'react-loader-spinner';
import { ColorsArray } from './Gradients';
import { EyeRegular, EyeOffRegular } from "@fluentui/react-icons";
import { MachineCard } from '../components/Cards/Machine/MachineCard';
import { MachineSearch, MachineSearchRef } from '../components/MachineSearch';
import { useSearchToggle } from '../hooks/useKeyboardShortcut';
import { getTeamsSubPageId } from '../TeamsHost';
import { useParams } from 'react-router-dom';
import { useTourContext } from '../contexts/TourContext';
import { SitemapTour } from '../tours/SitemapTour';
import { TourButton } from '../components/TourButton';

const FactoryView: React.FC = () => {
    const { machineId } = useParams<{ machineId: string }>();
    const [teamsMachineId, setTeamsMachineId] = useState<string | null>(null);
    const { registerTour, unregisterTour } = useTourContext();

    const [maxValue, setMaxValue] = React.useState(0);
    const [checkedIndex] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isAutoRefreshLoading, setIsAutoRefreshLoading] = React.useState(false);
    const factorySceneRef = React.useRef<FactoryScene | null>(null);
    const machineSearchRef = React.useRef<MachineSearchRef>(null);
    const [isHeatmapCurrentlyVisible, setIsHeatmapCurrentlyVisible] = React.useState(true);
    const [availableMachines, setAvailableMachines] = React.useState<string[]>([]);
    const [selectedMachine, setSelectedMachine] = React.useState<{ machineId?: string, elementGuid?: string } | null>(null);
    const [isHeatmapLocked, setIsHeatmapLocked] = React.useState(false);
    const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = React.useState(false);
    const [refreshFrequency, setRefreshFrequency] = React.useState<number>(180000);
    const autoRefreshIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
    const frequencyOptions = [
        { label: '10s', value: 10000 },
        { label: '30s', value: 30000 },
        { label: '1m', value: 60000 },
        { label: '2m', value: 120000 },
        { label: '3m', value: 180000 },
        { label: '15m', value: 900000 },
    ];

    React.useEffect(() => {
        registerTour(SitemapTour);
        return () => {
            unregisterTour(SitemapTour.id);
        };
    }, [registerTour, unregisterTour]);

    React.useEffect(() => {
        if (autoRefreshIntervalRef.current) {
            clearInterval(autoRefreshIntervalRef.current);
            autoRefreshIntervalRef.current = null;
        }

        if (isAutoRefreshEnabled) {

            autoRefreshIntervalRef.current = setInterval(() => {
                factorySceneRef.current?.refreshHeatMap();
                setIsAutoRefreshLoading(true);
            }, refreshFrequency);
        }

        return () => {
            if (autoRefreshIntervalRef.current) {
                clearInterval(autoRefreshIntervalRef.current);
            }
        };
    }, [isAutoRefreshEnabled, refreshFrequency]);

    useSearchToggle(() => {
        if (machineSearchRef.current) {
            machineSearchRef.current.focus();
        }
    });

    React.useEffect(() => {
        const getTeamsContext = async () => {
            try {
                const subPageId = await getTeamsSubPageId();
                if (subPageId) {
                    setTeamsMachineId(subPageId);
                }
            } catch (error) {
                console.error('Failed to get Teams context:', error);
            }
        };

        getTeamsContext();
    }, []);

    const activeMachineId = machineId || teamsMachineId;


    React.useEffect(() => {
        if (activeMachineId) {
            factorySceneRef.current?.selectMachine(activeMachineId);
        }
    }, [activeMachineId]);


    React.useEffect(() => {
        if (factorySceneRef.current) {
            return;
        }
        factorySceneRef.current = new FactoryScene();
        factorySceneRef.current.setHeatmapUpdateDelegate((x) => {
            setMaxValue(x);
            setIsLoading(false);
            setIsAutoRefreshLoading(false);
        });

        factorySceneRef.current.setOnMachineSelectedCallback((machineId?: string, elementGuid?: string) => {
            setSelectedMachine({ machineId, elementGuid });
        });

        let cancelled = false;
        const updateAvailableMachines = () => {
            if (cancelled) {
                return;
            }
            if (factorySceneRef.current) {
                const machines = factorySceneRef.current.getAvailableMachines();
                setAvailableMachines(machines);

                if (machines.length > 0) {
                    return;
                }
            }

            setTimeout(updateAvailableMachines, 1000);
        };

        setTimeout(updateAvailableMachines, 1000);

        factorySceneRef.current.animate();

        // In the browser the app switches tabs in-app instead of through separate Teams tab iframes,
        // so the scene has to be torn down when leaving the view.
        return () => {
            cancelled = true;
            factorySceneRef.current?.dispose();
            factorySceneRef.current = null;
        };
    }, []);

    const handleToggleHeatmap = () => {
        if (factorySceneRef.current) {
            factorySceneRef.current.toggleHeatmapVisibility();
            setIsHeatmapCurrentlyVisible(!isHeatmapCurrentlyVisible);
            if (!isHeatmapCurrentlyVisible && factorySceneRef.current.getCurrentPowerCategory() !== "None") {
                setIsLoading(true);
            }
        }
    };

    const handleMachineSearch = (machineId: string) => {


        if (!validateMachineId(machineId)) {
            return;
        }

        if (factorySceneRef.current) {
            const success = factorySceneRef.current.searchAndSelectMachine(machineId);
            if (success) {

                if (machineSearchRef.current) {
                    machineSearchRef.current.clear();
                }
            } else {
                console.warn('FactoryView: Machine search failed for:', machineId);
            }
        }
    };

    const getSearchSuggestions = (searchTerm: string): string[] => {
        if (factorySceneRef.current && searchTerm.length > 0) {
            return factorySceneRef.current.getSearchSuggestions(searchTerm, 4);
        }
        return [];
    };

    const validateMachineId = (machineId: string): boolean => {
        if (factorySceneRef.current) {
            const availableMachines = factorySceneRef.current.getAvailableMachines();
            return availableMachines.includes(machineId);
        }
        return false;
    };

    return <>
        <div {...factorySceneRef.current?.getRendererDomElement}></div>
        {/* Zero-height anchor for the overlays, so they start below the app tab bar when running in a browser. */}
        <div className="relative">

            <div className="absolute top-32 right-10 flex items-center gap-4 z-20">
                <Button
                    className='bg-primary-light dark:bg-primary-dark text-light '
                    data-tour="toggle-heatmap"
                    icon={isHeatmapCurrentlyVisible ? <EyeRegular /> : <EyeOffRegular />}
                    onClick={handleToggleHeatmap}
                    title={isHeatmapCurrentlyVisible ? "Hide Heatmap" : "Show Heatmap"}
                />



                <TourButton
                    className='bg-primary-light dark:bg-primary-dark text-light '
                    tourId={SitemapTour.id}
                    label="Tour"

                />
            </div>




            <>
                <MachineSearch
                    onMachineSelect={handleMachineSearch}
                    availableMachines={availableMachines}
                    getSuggestions={getSearchSuggestions}
                    ref={machineSearchRef}
                />
                {isHeatmapCurrentlyVisible && (
                    <div className='relative top-14'>
                        <div className="absolute top-0 right-0 pt-4 pr-10 space-x-2 flex flex-row z-10">
                            <span className='pt-1 pr-2 text-light'>Filter</span>
                            <div data-tour="heatmap-filter">
                                <ToggleButtonGroup keys={["None", "Order", "Inserts"]} checkedIndex={checkedIndex} action={(x) => {
                                    factorySceneRef.current?.ShowHeatMap(x);
                                    setIsLoading(true);
                                }} />
                            </div>
                        </div>
                        <div className="absolute top-28 right-0 pt-4 pr-10 space-x-2 flex flex-col z-10  space-y-4">
                            <div className='flex justify-end'>
                                <Button
                                    data-tour="refresh-heatmap"
                                    icon={<ArrowSync16Regular />}
                                    onClick={() => {
                                        factorySceneRef.current?.refreshHeatMap();
                                        setIsLoading(true);
                                    }}
                                    title="Refresh heatmap now"
                                />
                                <Button
                                    data-tour="auto-refresh-toggle"
                                    icon={isAutoRefreshEnabled ? <ArrowClockwise16Regular /> : <Pause16Regular />}
                                    appearance={isAutoRefreshEnabled ? "primary" : "secondary"}
                                    onClick={() => {
                                        const newState = !isAutoRefreshEnabled;
                                        setIsAutoRefreshEnabled(newState);
                                    }}
                                    title={isAutoRefreshEnabled ? `Auto-refresh enabled - Click to disable` : "Enable auto-refresh"}
                                />
                            </div>

                            {isAutoRefreshEnabled && (
                                <Dropdown
                                    data-tour="refresh-frequency"
                                    value={frequencyOptions.find(opt => opt.value === refreshFrequency)?.label}
                                    selectedOptions={[frequencyOptions.find(opt => opt.value === refreshFrequency)?.label || '3m']}
                                    onOptionSelect={(_, data) => {
                                        const selected = frequencyOptions.find(opt => opt.label === data.optionValue);
                                        if (selected) {
                                            setRefreshFrequency(selected.value);
                                        }
                                    }}
                                    size="small"
                                    title="Auto-refresh frequency"
                                    style={{ minWidth: '70px' }}
                                >
                                    {frequencyOptions.map((option) => (
                                        <Option key={option.value} value={option.label}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Dropdown>
                            )}
                            {isHeatmapCurrentlyVisible && (
                                <div className="flex flex-row  z-10">
                                    <div data-tour="heatmap-scale" className='flex flex-col space-y-4'>
                                        <HeatGuide maxValue={maxValue} />
                                        <div className="flex justify-end">
                                            <Button
                                                data-tour="lock-scale"
                                                icon={isHeatmapLocked ? <LockClosed20Regular /> : <LockOpen20Regular />}
                                                title={isHeatmapLocked ? "Unlock heatmap scaling" : "Lock current heatmap scaling"}
                                                onClick={() => {
                                                    factorySceneRef.current?.lockHeatmapScaling(!isHeatmapLocked);
                                                    factorySceneRef.current?.refreshHeatmapWithoutFetch();
                                                    setIsHeatmapLocked(!isHeatmapLocked);
                                                }}
                                            />
                                        </div>

                                    </div>


                                </div>

                            )}
                        </div>
                    </div>
                )}
            </>



            {
                (isLoading && !isAutoRefreshLoading) ? <div className='fixed flex top-1/2 left-1/2 -mt-[150px] -ml-[150px] justify-center items-center'>
                    <div>
                        <Oval color='#ffffff' secondaryColor='#ffffff' height={150} width={150} />
                    </div>
                </div > : null
            }


            {selectedMachine && (
                <div className="absolute max-sm:w-72 w-96 z-50  top-20 left-0 h-screen bg-primary-light dark:bg-primary-dark">
                    <div className=" mr-2 mt-2 grow text-right z-10">
                        <Dismiss20Regular onClick={() => setSelectedMachine(null)} />
                    </div>
                    <div className="pt-2 overflow-y-auto  h-[calc(100vh-2rem)] ">
                        {selectedMachine.elementGuid ?
                            <MachineCard
                                name={selectedMachine.machineId || "Selected Machine"}
                                elementGuid={selectedMachine.elementGuid}
                                type='machine'
                            />
                            : <div className='flex justify-center pt-10 mb-12'>{selectedMachine.machineId || "Selected machine"} does not have any data to show</div>}
                    </div>

                </div>
            )}
        </div>
    </>;
}


const HeatGuide = (props: { maxValue: number }) => {
    const guideHeight = window.innerHeight / 2;

    return <>
        <div className="text-light flex flex-row rounded-md box-border w-32 justify-end">
            <div className='flex flex-col justify-between mr-2' style={{ height: guideHeight }}>
                <div className='space-x-1 flex flex-row'>
                    <span>{props.maxValue.toFixed(2)}</span>
                    <span>kW</span>
                </div>
                <div className='space-x-1 flex flex-row'>
                    <span>{(props.maxValue * 0.66).toFixed(2)}</span>
                    <span>kW</span>
                </div>
                <div className='space-x-1 flex flex-row'>
                    <span>{(props.maxValue * 0.5).toFixed(2)}</span>
                    <span>kW</span>
                </div>
                <div className='space-x-1 flex flex-row'>
                    <span>{(props.maxValue * 0.33).toFixed(2)}</span>
                    <span>kW</span>
                </div>
                <div className='space-x-1 flex flex-row'>
                    <span>0.00</span>
                    <span>kW</span>
                </div>
            </div>
            <div className=' box-border border-2 border-primary-dark'>
                <Surface width={35} height={guideHeight}>
                    <Node shader={HeatGuideShader()} uniforms={{
                        colors: ColorsArray.map(c => new Color(c)).concat(Array(100 - ColorsArray.length).fill(new Color(0))),
                        numColors: ColorsArray.length,
                    }} />;
                </Surface>
            </div>

        </div>
    </>
}


export default FactoryView;
